import React, { useEffect, useMemo, useState } from 'react';

import { useAbility } from '@casl/react';
import { Translate, useTranslate } from '@bynder/localization';
import { Dropdown, Button } from '@bynder/design-system';
import { IconAdd } from '@bynder/icons';

import { isView, isMainView } from '../../../../helpers/windowLocation';
import {
	openWFModal,
	getWorkflowData,
	initWFModalContainer,
	initModalLinks,
} from '../../../../legacy/legacy';
import { AbilityContext, Can } from '../../../../abilities/Can';

import { WFPresets, DropdownItem } from './types';
import { createNewPreset } from './helpers';
import { openWorkflowModalOnLoad } from '../openPresetModalOnload';

const workflow = getWorkflowData();

const filterPresets = (presets: DropdownItem[], _searchValue: string) =>
	presets.filter(({ label }) => label.toLowerCase().includes(_searchValue.trim().toLowerCase()));

export function CreateNewCTA() {
	const ability = useAbility(AbilityContext);
	const { translate } = useTranslate();

	// Open the workflow modal on load if a preset is specified in the URL
	openWorkflowModalOnLoad(workflow.visiblePresets);

	const [wfPresets, setWfPresets] = useState<WFPresets>({
		job: [],
		campaign: [],
	});
	const [searchValue, setSearchValue] = useState<string>('');

	const hasPresets: boolean = wfPresets.job.length > 0 || wfPresets.campaign.length > 0;

	useEffect(() => {
		if (hasPresets) {
			initModalLinks();
			initWFModalContainer();
		}
	}, [hasPresets]);

	useEffect(() => {
		if (ability.can('select', 'workflow_use_preset')) {
			const newPresets: WFPresets = workflow.visiblePresets.reduce(
				(acc, preset) => {
					return {
						...acc,
						[preset.type]: [...acc[preset.type], createNewPreset(preset)],
					};
				},

				{ job: [], campaign: [] },
			);

			setWfPresets(newPresets);
		}
	}, [ability]);

	const showCreateNew =
		ability.can('show', 'workflow_create_new') &&
		(isMainView() ||
			isView('main/index') ||
			isView('campaign/overview') ||
			isView('campaign/dashboard') ||
			isView('campaign/calendar') ||
			isView('job/overview'));

	const filteredPresets: WFPresets = useMemo(
		() => ({
			campaign: filterPresets(wfPresets.campaign, searchValue),
			job: filterPresets(wfPresets.job, searchValue),
		}),
		[wfPresets, searchValue],
	);

	// TODO: Can we fix the width of the button regardsless the content?
	// when searching it changes
	return showCreateNew ? (
		<Dropdown
			minWidth="336px"
			trigger={({ isOpen, ...triggerProps }) => (
				<Button
					variant="primary"
					isPressed={isOpen}
					rightIcon={<Dropdown.Arrow />}
					{...triggerProps}
				>
					{translate('workflow.CreateNew')}...
				</Button>
			)}
		>
			{hasPresets && (
				<Can I="select" a="workflow_use_preset">
					<Dropdown.SearchInput
						value={searchValue}
						onChange={(value) => {
							setSearchValue(value);
						}}
						placeholder={translate('generalSearch')}
						aria-label="Search for options"
					/>
				</Can>
			)}

			{/* add new job/campaign */}
			<Can I="select" a="workflow_add_job">
				<Dropdown.Item
					onClick={() => {
						const idURL = workflow.campaignId ? workflow.campaignId + '/' : '';

						openWFModal({
							url: '/workflow/job/add/' + idURL,
							buttonId: 'job-add-from-react-header',
							dataInit: 'Job.new',
						});
					}}
					icon={<IconAdd />}
				>
					<span id="job-add-from-react-header">
						<Translate id="workflow.Job" />
					</span>
				</Dropdown.Item>
			</Can>
			<Can I="select" a="workflow_add_campaign">
				<Dropdown.Item
					onClick={() =>
						openWFModal({
							url: '/workflow/campaign/add/',
							buttonId: 'campaign-add-from-react-header',
							dataInit: 'Campaign.new',
						})
					}
					icon={<IconAdd />}
				>
					<span id="campaign-add-from-react-header">
						<Translate id="workflow.Campaign" />
					</span>
				</Dropdown.Item>
			</Can>
			<Dropdown.Divider />
			<Can I="select" a="workflow_use_preset">
				{/* job/campaign presets */}

				{filteredPresets.campaign.length > 0 && (
					<>
						<Dropdown.SectionTitle>
							<Translate id="workflow.CampaignPresets" /> (
							{filteredPresets.campaign.length})
						</Dropdown.SectionTitle>
						{filteredPresets.campaign.map(({ id, label, dataInit, url }) => (
							<Dropdown.Item
								role="menuitem"
								onClick={() => openWFModal({ url, buttonId: id, dataInit })}
								key={id}
							>
								<span id={id}>{label}</span>
							</Dropdown.Item>
						))}
					</>
				)}

				{filteredPresets.job.length > 0 && filteredPresets.campaign.length > 0 && (
					<Dropdown.Divider />
				)}

				{filteredPresets.job.length > 0 && (
					<>
						<Dropdown.SectionTitle>
							<Translate id="workflow.JobPresets" /> ({filteredPresets.job.length})
						</Dropdown.SectionTitle>
						{filteredPresets.job.map(({ id, label, dataInit, url }) => (
							<Dropdown.Item
								role="menuitem"
								onClick={() => openWFModal({ url, buttonId: id, dataInit })}
								key={id}
							>
								<span id={id}>{label}</span>
							</Dropdown.Item>
						))}
					</>
				)}
			</Can>
		</Dropdown>
	) : null;
}
